import React, { useState, useEffect, useCallback, useRef } from "react";
import DatePicker from "react-datepicker";
import axios from "axios";
import styles from "./PumpDetailsModal.module.css";

interface Assignment {
  fleet: number | null;
  district: string | null;
  pad_id: string | null;
  start_ts: number | null;
  end_ts: number | null;
}

interface PumpHistory {
  pumpId: string;
  assignments: Assignment[];
}

interface PumpDetailsModalProps {
  show: boolean;
  onHide: () => void;
  theme: string;
  pumpNames: string[];
  initialFromDate: Date | null;
  initialToDate: Date | null;
}

const PumpDetailsModal: React.FC<PumpDetailsModalProps> = ({
  show,
  onHide,
  theme,
  pumpNames,
  initialFromDate,
  initialToDate,
}) => {
  const [fromDate, setFromDate] = useState<Date | null>(initialFromDate);
  const [toDate, setToDate] = useState<Date | null>(initialToDate);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pumpHistory, setPumpHistory] = useState<PumpHistory[]>([]);
  const [selectedPump, setSelectedPump] = useState<string | null>(null);
  const [expandedPumps, setExpandedPumps] = useState<Set<string>>(new Set());
  const [filterDistrict, setFilterDistrict] = useState<string>("all");
  const [filterFleet, setFilterFleet] = useState<string>("all");
  const modalRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api";

  const districtColors: Record<string, string> = {
    Northeast: "#6b7280",
    Vernal: "#9ca3af",
    Longview: "#4b5563",
    Pleasanton: "#d1d5db",
    Midland: "#374151",
  };

  // Get unique districts from data
  const uniqueDistricts = Array.from(
    new Set(
      pumpHistory.flatMap((p) =>
        p.assignments
          .map((a) => a.district)
          .filter((d): d is string => d !== null),
      ),
    ),
  ).sort();

  const uniqueFleets = Array.from(
    new Set(
      pumpHistory.flatMap((p) =>
        p.assignments
          .map((a) => a.fleet)
          .filter((f): f is number => f !== null),
      ),
    ),
  ).sort((a, b) => a - b);

  useEffect(() => {
    setFromDate(initialFromDate);
    setToDate(initialToDate);
  }, [initialFromDate, initialToDate]);

  const fetchFleetHistory = useCallback(async () => {
    if (pumpNames.length === 0) return;

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      const params: Record<string, any> = {
        pump_names: pumpNames.join(","),
      };

      if (fromDate) {
        params.start_ts = fromDate.getTime();
      }
      if (toDate) {
        params.end_ts = toDate.getTime();
      }

      const response = await axios.get(`${backendUrl}/pumps/fleet-history`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setPumpHistory(response.data);
      setLoading(false);
    } catch (err: any) {
      console.error("Error fetching fleet history:", err);
      setError(err.response?.data?.error || "Failed to fetch fleet history");
      setLoading(false);
    }
  }, [backendUrl, pumpNames, fromDate, toDate]);

  useEffect(() => {
    if (show && pumpNames.length > 0) {
      fetchFleetHistory();
    }
  }, [show, pumpNames, fetchFleetHistory]);

  useEffect(() => {
    const scrollBarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    const appScrollContainer = document.querySelector(
      '[data-modal-scroll-lock="fleet-main-content"]',
    ) as HTMLElement | null;

    if (show) {
      document.body.style.overflow = "hidden";
      document.body.style.paddingRight = `${scrollBarWidth}px`;
      document.documentElement.style.overflow = "hidden";

      if (appScrollContainer) {
        appScrollContainer.style.overflow = "hidden";
      }
    } else {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.overflow = "";

      if (appScrollContainer) {
        appScrollContainer.style.overflow = "hidden";
      }
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.documentElement.style.overflow = "";

      if (appScrollContainer) {
        appScrollContainer.style.overflow = "hidden";
      }
    };
  }, [show]);

  useEffect(() => {
    if (!show) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onHide();
        return;
      }

      if (e.key === "Tab" && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );

        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement as HTMLElement | null;

        if (e.shiftKey) {
          if (active === first || !modalRef.current.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last || !modalRef.current.contains(active)) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [show, onHide]);

  useEffect(() => {
    if (show) {
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 0);
    }
  }, [show]);

  const filteredPumps = pumpHistory.filter((pump) => {
    const matchesDistrict =
      filterDistrict === "all" ||
      pump.assignments.some((a) => a.district === filterDistrict);
    const matchesFleet =
      filterFleet === "all" ||
      pump.assignments.some((a) => a.fleet?.toString() === filterFleet);
    return matchesDistrict && matchesFleet;
  });

  const tableData = (
    selectedPump
      ? filteredPumps.filter((p) => p.pumpId === selectedPump)
      : filteredPumps
  )
    .flatMap((pump) =>
      pump.assignments
        .filter((a) => {
          const matchesDistrict =
            filterDistrict === "all" || a.district === filterDistrict;
          const matchesFleet =
            filterFleet === "all" || a.fleet?.toString() === filterFleet;
          return matchesDistrict && matchesFleet;
        })
        .map((a) => ({
          pumpId: pump.pumpId,
          ...a,
        })),
    )
    .sort((a, b) => (b.start_ts || 0) - (a.start_ts || 0));

  const togglePumpRow = (pumpId: string) => {
    setExpandedPumps((prev) => {
      const next = new Set(prev);
      if (next.has(pumpId)) {
        next.delete(pumpId);
      } else {
        next.add(pumpId);
      }
      return next;
    });
  };

  const groupedPumpRows = filteredPumps.map((pump) => {
    const assignments = pump.assignments
      .filter((a) => {
        const matchesDistrict =
          filterDistrict === "all" || a.district === filterDistrict;
        const matchesFleet =
          filterFleet === "all" || a.fleet?.toString() === filterFleet;
        return matchesDistrict && matchesFleet;
      })
      .sort((a, b) => (b.start_ts || 0) - (a.start_ts || 0));

    const latestAssignment = assignments[0] || null;

    return {
      pumpId: pump.pumpId,
      assignments,
      latestAssignment,
    };
  });

  const getTimelineBounds = () => {
    if (fromDate && toDate) {
      return { start: fromDate.getTime(), end: toDate.getTime() };
    }

    let minTs = Infinity;
    let maxTs = -Infinity;

    pumpHistory.forEach((pump) => {
      pump.assignments.forEach((a) => {
        if (a.start_ts && a.start_ts < minTs) minTs = a.start_ts;
        if (a.end_ts && a.end_ts > maxTs) maxTs = a.end_ts;
        if (!a.end_ts) maxTs = Date.now();
      });
    });

    if (minTs === Infinity) minTs = Date.now() - 30 * 24 * 60 * 60 * 1000;
    if (maxTs === -Infinity) maxTs = Date.now();

    return { start: minTs, end: maxTs };
  };

  const timelineBounds = getTimelineBounds();
  const totalDuration = timelineBounds.end - timelineBounds.start;

  const getBarPosition = (startTs: number | null, endTs: number | null) => {
    const start = startTs || timelineBounds.start;
    const end = endTs || Date.now();

    const left = ((start - timelineBounds.start) / totalDuration) * 100;
    const width = ((end - start) / totalDuration) * 100;

    return {
      left: `${Math.max(0, left)}%`,
      width: `${Math.max(1, Math.min(100 - left, width))}%`,
    };
  };

  const formatDate = (ts: number | null) => {
    if (!ts) return "Present";
    return new Date(ts).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDateTime = (ts: number | null) => {
    if (!ts) return "Present";
    return new Date(ts).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getDuration = (startTs: number | null, endTs: number | null) => {
    const start = startTs || Date.now();
    const end = endTs || Date.now();
    const days = Math.round((end - start) / (1000 * 60 * 60 * 24));
    return `${days} days`;
  };

  const getTimelineLabels = () => {
    const bounds = timelineBounds;
    const labels: string[] = [];
    const duration = bounds.end - bounds.start;
    const numLabels = 6;

    for (let i = 0; i < numLabels; i++) {
      const ts = bounds.start + (duration * i) / (numLabels - 1);
      const date = new Date(ts);
      labels.push(
        date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      );
    }

    return labels;
  };

  const getDistrictColor = (district: string | null) => {
    if (!district) return "#6b7280";
    return districtColors[district] || "#6b7280";
  };

  if (!show) return null;

  const isDark = theme === "dark";

  return (
    <>
      <div className={styles.backdrop} onClick={onHide} />

      <div
        ref={modalRef}
        className={`${styles.modal} ${isDark ? styles.dark : styles.light}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pump-details-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.stickyTop}>
          <div className={styles.header}>
            <div>
              <h2 id="pump-details-title" className={styles.title}>
                Pump Fleet Assignment History
              </h2>
            </div>

            <div className={styles.headerRight}>
              <span className={styles.pumpCount}>
                {filteredPumps.length} pumps
              </span>
              <button
                ref={closeButtonRef}
                className={styles.closeButton}
                onClick={onHide}
                aria-label="Close pump details modal"
              >
                ×
              </button>
            </div>
          </div>

          <div className={styles.controlsBar}>
            <div className={styles.controlGroup}>
              <label>From:</label>
              <DatePicker
                selected={fromDate}
                onChange={(date: Date | null) => setFromDate(date)}
                showTimeSelect
                dateFormat="MMM d, yyyy h:mm aa"
                className={styles.datePicker}
              />
            </div>

            <div className={styles.controlGroup}>
              <label>To:</label>
              <DatePicker
                selected={toDate}
                onChange={(date: Date | null) => setToDate(date)}
                showTimeSelect
                dateFormat="MMM d, yyyy h:mm aa"
                className={styles.datePicker}
              />
            </div>

            <button
              className={styles.refreshButton}
              onClick={fetchFleetHistory}
              disabled={loading}
            >
              {loading ? "Loading..." : "Refresh"}
            </button>

            <div className={styles.controlGroup}>
              <label>District:</label>
              <select
                value={filterDistrict}
                onChange={(e) => setFilterDistrict(e.target.value)}
                className={styles.select}
              >
                <option value="all">All Districts</option>
                {uniqueDistricts.map((district) => (
                  <option key={district} value={district}>
                    {district}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.controlGroup}>
              <label>Fleet:</label>
              <select
                value={filterFleet}
                onChange={(e) => setFilterFleet(e.target.value)}
                className={styles.select}
              >
                <option value="all">All Fleets</option>
                {uniqueFleets.map((fleet) => (
                  <option
                    key={fleet}
                    value={fleet.toString()}
                  >{`Fleet ${fleet}`}</option>
                ))}
              </select>
            </div>

            <div className={styles.legend}>
              {Object.entries(districtColors).map(([district, color]) => (
                <div key={district} className={styles.legendItem}>
                  <div
                    className={styles.legendColor}
                    style={{ backgroundColor: color }}
                  />
                  <span>{district}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.content}>
          {loading && (
            <div className={styles.loadingOverlay}>
              <div className={styles.spinner} />
              <span>Loading fleet history...</span>
            </div>
          )}

          {error && (
            <div className={styles.errorMessage}>
              <span>
                ⚠️{" "}
                {error === "end_ts must be > start_ts"
                  ? "Please select a valid date range. End date should be after start date."
                  : error === "Provide both start_ts and end_ts together"
                    ? "Please select both start date and end date."
                    : error}
              </span>
              <button onClick={fetchFleetHistory}>Retry</button>
            </div>
          )}

          {!loading && !error && filteredPumps.length === 0 && (
            <div className={styles.emptyState}>
              <span>
                No fleet assignment history found for the selected pumps and
                filters.
              </span>
            </div>
          )}

          {!loading && !error && filteredPumps.length > 0 && (
            <>
              <div className={styles.tableSection}>
                <div className={styles.sectionHeader}>
                  <h3>Assignment Details</h3>
                  <span className={styles.recordCount}>
                    {groupedPumpRows.length} pumps
                  </span>
                </div>

                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Pump</th>
                        <th>Fleet</th>
                        <th>District</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Duration</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedPumpRows.map((row, idx) => {
                        const isExpanded = expandedPumps.has(row.pumpId);
                        const latest = row.latestAssignment;

                        return (
                          <React.Fragment key={row.pumpId}>
                            <tr
                              className={idx % 2 === 1 ? styles.alternate : ""}
                              onClick={() => togglePumpRow(row.pumpId)}
                              style={{ cursor: "pointer" }}
                            >
                              <td className={styles.pumpCell}>
                                <span style={{ marginRight: 8 }}>
                                  {isExpanded ? "▼" : "▶"}
                                </span>
                                {row.pumpId}
                              </td>

                              <td>
                                {latest?.fleet
                                  ? `Fleet ${latest.fleet}`
                                  : "N/A"}
                              </td>

                              <td>
                                <div className={styles.districtCell}>
                                  <div
                                    className={styles.districtDot}
                                    style={{
                                      backgroundColor: getDistrictColor(
                                        latest?.district || null,
                                      ),
                                    }}
                                  />
                                  <span>{latest?.district || "Unknown"}</span>
                                </div>
                              </td>

                              <td>{formatDate(latest?.start_ts ?? null)}</td>
                              <td>{formatDate(latest?.end_ts ?? null)}</td>
                              <td>
                                {getDuration(
                                  latest?.start_ts ?? null,
                                  latest?.end_ts ?? null,
                                )}
                              </td>
                              <td>
                                {!latest?.end_ts ? (
                                  <span className={styles.activeStatus}>
                                    Active
                                  </span>
                                ) : (
                                  <span className={styles.completedStatus}>
                                    Completed
                                  </span>
                                )}
                              </td>
                            </tr>

                            {isExpanded && (
                              <tr>
                                <td colSpan={7} style={{ padding: 0 }}>
                                  <div className={styles.inlineTimelineWrap}>
                                    {row.assignments.map((assignment, aIdx) => {
                                      const pos = getBarPosition(
                                        assignment.start_ts,
                                        assignment.end_ts,
                                      );

                                      return (
                                        <div
                                          key={aIdx}
                                          className={styles.inlineTimelineRow}
                                        >
                                          <div
                                            className={
                                              styles.inlineTimelineTrack
                                            }
                                          >
                                            <div
                                              className={
                                                styles.inlineTimelineBar
                                              }
                                              style={{
                                                ...pos,
                                                backgroundColor:
                                                  getDistrictColor(
                                                    assignment.district,
                                                  ),
                                              }}
                                              title={`${assignment.district || "Unknown"} | ${formatDateTime(
                                                assignment.start_ts,
                                              )} - ${formatDateTime(assignment.end_ts)}`}
                                            >
                                              <span
                                                className={
                                                  styles.inlineTimelineBarLabel
                                                }
                                              >
                                                {assignment.fleet
                                                  ? `F${assignment.fleet}`
                                                  : "N/A"}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PumpDetailsModal;
